import { tapResponse } from "@ngrx/operators";
import { patchState, WritableStateSource } from "@ngrx/signals";
import { rxMethod, RxMethod } from "@ngrx/signals/rxjs-interop";
import { Cart, CartItem, CartTaxFormValues, ValidatedCart } from "@ph-shopping/model/cart";
import { GetProductsRequest } from "@ph-shopping/model/product.request";
import { UserStoreType } from "@ph-store/user/user.store";
import { of, pipe } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { CartApiClient } from "../cart-api.client";
import { calculateCartTotal } from "../cart.helpers";
import { ProductItem } from "@ph-model/response/product-item.model";
import { productIdGenerator } from '@ph-shared/utils/product-id-generator';

export interface CartStoreForMethods extends WritableStateSource<object> {
  validatedCart(): CartItem[];
  cart(): CartItem[];
  cartTaxFormValues(): CartTaxFormValues[];
}

export interface CartStoreCartDeps {
  userStore: UserStoreType;
  cartApiClient: CartApiClient;
}

export interface CartStoreCartMethodsReturn {
  addToCart: RxMethod<{ cartItem: CartItem; productsRequest: GetProductsRequest }>;
  deleteFromCart: RxMethod<{ cartItem: CartItem; productsRequest: GetProductsRequest }>;
  reconcileCartWithLoadedProducts: RxMethod<{ productsRequest: GetProductsRequest; loadedProducts: ProductItem[] }>;
}

export function withCartMethods(
  store: CartStoreForMethods,
  deps: CartStoreCartDeps
): CartStoreCartMethodsReturn {
  const { userStore, cartApiClient } = deps;

  const addToCart = rxMethod<{ cartItem: CartItem; productsRequest: GetProductsRequest }>(
    pipe(
      switchMap(({ cartItem, productsRequest }) => {
        const user = userStore.user();
        if (!user) {
          console.error('User not found. Add to cart aborted.');
          return of([]);
        }
        const cart = [...store.validatedCart(), JSON.parse(JSON.stringify(cartItem))];

        const request = new Cart(cart, productsRequest, user);
        patchState(store, {
          cart: [...store.cart(), JSON.parse(JSON.stringify(cartItem))],
          cartTotal: calculateCartTotal(store.cartTaxFormValues()),
          savedQuote: false,
        });

        return cartApiClient.validateCart(request).pipe(
          tapResponse<ValidatedCart>({
            next: (validatedCart: ValidatedCart) => {
              patchState(store, {
                validatedCart: validatedCart.validatedCart,
                success: true,
                savedQuote: false,
              });
            },
            error: (error: unknown) => {
              const errorMessage =
                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                  ? error.error
                  : String(error)) || '';
              console.error(errorMessage);

              patchState(store, {
                error: errorMessage,
                success: false,
                savedQuote: false,
              });
            },
          })
        );
      })
    )
  );

  const deleteFromCart = rxMethod<{ cartItem: CartItem; productsRequest: GetProductsRequest }>(
    pipe(
      switchMap(({ cartItem, productsRequest }) => {
        const user = userStore.user();
        if (!user) {
          console.error('User not found. Delete from cart aborted.');
          return of([]);
        }
        const cart = store.validatedCart().filter((item: CartItem) => item.key !== cartItem.key);

        const request = new Cart(cart, productsRequest, user);

        patchState(store, {
          cart: store.cart().filter((item: CartItem) => item.key !== cartItem.key),
          cartTaxFormValues: store
            .cartTaxFormValues()
            .filter((item: CartTaxFormValues) => item.key !== cartItem.key),
          validatedCart: store.validatedCart().filter((item) => item.key !== cartItem.key),
          cartTotal: calculateCartTotal(
            store.cartTaxFormValues().filter((item: CartTaxFormValues) => item.key !== cartItem.key)
          ),
          savedQuote: false,
        });

        return cartApiClient.validateCart(request).pipe(
          tapResponse<ValidatedCart>({
            next: (validatedCart: ValidatedCart) => {
              patchState(store, {
                validatedCart: validatedCart.validatedCart,
                success: true,
                savedQuote: false,
              });
            },
            error: (error: unknown) => {
              const errorMessage =
                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                  ? error.error
                  : String(error)) || '';
              console.error(errorMessage);

              patchState(store, {
                error: errorMessage,
                success: false,
                savedQuote: false,
              });
            },
          })
        );
      })
    )
  );

  const reconcileCartWithLoadedProducts = rxMethod<{
    productsRequest: GetProductsRequest;
    loadedProducts: ProductItem[];
  }>(
    pipe(
      switchMap(({ productsRequest, loadedProducts }) => {
        const user = userStore.user();
        if (!user) {
          console.error('User not found. Reconcile cart aborted.');
          return of([]);
        }
        const currentCart = store.validatedCart();
        const matchedCart: CartItem[] = currentCart.map((cartItem) => {
          const matchedProduct = loadedProducts.find(
            (p) => productIdGenerator(p) === productIdGenerator(cartItem.product)
          );
          if (matchedProduct) {
            return { ...cartItem, product: matchedProduct };
          }
          return cartItem;
        });
        const request = new Cart(matchedCart, productsRequest, user);

        return cartApiClient.validateCart(request).pipe(
          tapResponse<ValidatedCart>({
            next: (validatedCart: ValidatedCart) => {
              patchState(store, {
                cart: validatedCart.validatedCart,
                validatedCart: validatedCart.validatedCart,
                success: true,
              });
            },
            error: (error: unknown) => {
              const errorMessage =
                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                  ? error.error
                  : String(error)) || '';
              console.error(errorMessage);
              patchState(store, { error: errorMessage, success: false });
            },
          })
        );
      })
    )
  );



  return {
    addToCart,
    deleteFromCart,
    reconcileCartWithLoadedProducts
  }

}